
import { supabase } from '@/lib/supabase';

export interface BatchJob {
  id: string;
  type: 'token_refresh' | 'balance_sync' | 'pattern_refresh';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  total_items: number;
  processed_items: number;
  failed_items: number;
  progress_percentage: number;
  current_phase?: 'token_refresh' | 'balance_sync' | 'pattern_refresh';
  current_connection_id?: string;
  current_connection_name?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
  estimated_completion?: string;
}

export interface BatchProgress {
  jobId: string;
  status: string;
  progress: number;
  processed: number;
  total: number;
  failed: number;
  errors: Array<{ connectionId: string; error: string }>;
  estimatedTimeRemaining?: number;
}

export interface ConnectionResult {
  connectionId: string;
  apiName: string;
  status: 'pending' | 'processing' | 'success' | 'failed';
  error?: string;
  processedAt?: string;
}

class BatchProcessingService {
  private batchSize = 50; // Process 50 tokens at a time
  private maxConcurrentBatches = 3;
  private delayBetweenBatches = 2000; // 2 seconds delay to respect API rate limits

  async startBatchTokenRefresh(connectionIds: string[]): Promise<string> {
    const jobId = crypto.randomUUID();
    
    try {
      // Create batch job record
      const { error } = await supabase
        .from('batch_jobs')
        .insert({
          id: jobId,
          type: 'token_refresh',
          status: 'pending',
          total_items: connectionIds.length,
          processed_items: 0,
          failed_items: 0,
          progress_percentage: 0,
          current_phase: 'token_refresh'
        });

      if (error) throw error;

      // Start processing by calling the edge function with jobId
      this.processBatchTokenRefresh(jobId, connectionIds);
      
      return jobId;
    } catch (error) {
      console.error('Failed to start batch job:', error);
      throw error;
    }
  }

  private async processBatchTokenRefresh(jobId: string, connectionIds: string[]) {
    try {
      await this.updateJobStatus(jobId, 'running', {
        current_phase: 'token_refresh'
      });
      
      // Call the edge function with jobId for integrated processing
      const { data, error } = await supabase.functions.invoke('refresh-ctrader-tokens', {
        body: { 
          connectionIds: connectionIds,
          refreshAll: false,
          jobId: jobId  // Pass jobId to edge function
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        await this.updateJobStatus(jobId, 'failed', {
          error_message: error.message || 'Edge function failed'
        });
        return;
      }

      console.log('Batch token refresh completed:', data);
      
      // The edge function will handle updating the job status to completed
      // But we can also update final statistics here if needed
      
    } catch (error) {
      console.error('Batch processing failed:', error);
      await this.updateJobStatus(jobId, 'failed', {
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  async startBatchBalanceSync(connectionIds: string[]): Promise<string> {
    const jobId = crypto.randomUUID();
    
    try {
      const { error } = await supabase
        .from('batch_jobs')
        .insert({
          id: jobId,
          type: 'balance_sync',
          status: 'pending',
          total_items: connectionIds.length,
          processed_items: 0,
          failed_items: 0,
          progress_percentage: 0,
          current_phase: 'balance_sync'
        });

      if (error) throw error;

      this.processBatchBalanceSync(jobId, connectionIds);
      
      return jobId;
    } catch (error) {
      console.error('Failed to start batch balance sync:', error);
      throw error;
    }
  }

  private async processBatchBalanceSync(jobId: string, connectionIds: string[]) {
    try {
      await this.updateJobStatus(jobId, 'running', {
        current_phase: 'balance_sync'
      });
      
      // Call the edge function with jobId for integrated processing
      const { data, error } = await supabase.functions.invoke('sync-ctrader-accounts', {
        body: { 
          connectionIds: connectionIds,
          syncAll: false,
          jobId: jobId  // Pass jobId to edge function
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        await this.updateJobStatus(jobId, 'failed', {
          error_message: error.message || 'Edge function failed'
        });
        return;
      }

      console.log('Batch balance sync completed:', data);
      
      // The edge function will handle updating the job status to completed
      
    } catch (error) {
      console.error('Batch balance sync failed:', error);
      await this.updateJobStatus(jobId, 'failed', {
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  async getBatchJobStatus(jobId: string): Promise<BatchJob | null> {
    const { data, error } = await supabase
      .from('batch_jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (error) {
      console.error('Failed to get batch job status:', error);
      return null;
    }

    return data;
  }

  async cancelBatchJob(jobId: string): Promise<void> {
    await this.updateJobStatus(jobId, 'cancelled');
  }

  async getAllBatchJobs(limit = 50): Promise<BatchJob[]> {
    const { data, error } = await supabase
      .from('batch_jobs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Failed to get batch jobs:', error);
      return [];
    }

    return data || [];
  }

  async getJobsByStatus(status: BatchJob['status']): Promise<BatchJob[]> {
    const { data, error } = await supabase
      .from('batch_jobs')
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to get jobs by status:', error);
      return [];
    }

    return data || [];
  }

  private async updateJobStatus(
    jobId: string, 
    status: BatchJob['status'], 
    updates: Partial<BatchJob> = {}
  ) {
    const { error } = await supabase
      .from('batch_jobs')
      .update({
        status,
        updated_at: new Date().toISOString(),
        ...updates
      })
      .eq('id', jobId);

    if (error) {
      console.error('Failed to update job status:', error);
    }
  }

  async updateJobProgress(jobId: string, updates: Partial<BatchJob>) {
    const { error } = await supabase
      .from('batch_jobs')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', jobId);

    if (error) {
      console.error('Failed to update job progress:', error);
    }
  }

  async updateCurrentConnection(jobId: string, connectionId: string, connectionName: string) {
    await this.updateJobProgress(jobId, {
      current_connection_id: connectionId,
      current_connection_name: connectionName
    });
  }

  async startBatchPatternRefresh(botId: string, tradingFileId: string, dryRun = false): Promise<string> {
    const jobId = crypto.randomUUID();
    
    try {
      const { error } = await supabase
        .from('batch_jobs')
        .insert({
          id: jobId,
          type: 'pattern_refresh',
          status: 'pending',
          total_items: 1, // One pattern refresh task
          processed_items: 0,
          failed_items: 0,
          progress_percentage: 0,
          current_phase: 'pattern_refresh'
        });

      if (error) throw error;

      this.processBatchPatternRefresh(jobId, botId, tradingFileId, dryRun);
      
      return jobId;
    } catch (error) {
      console.error('Failed to start pattern refresh job:', error);
      throw error;
    }
  }

  private async processBatchPatternRefresh(jobId: string, botId: string, tradingFileId: string, dryRun: boolean) {
    try {
      await this.updateJobStatus(jobId, 'running', {
        current_phase: 'pattern_refresh'
      });
      
      // Call the pattern-refresh edge function with jobId
      const { data, error } = await supabase.functions.invoke('pattern-refresh', {
        body: { 
          botId,
          tradingFileId,
          dryRun,
          jobId // Pass jobId to edge function for progress updates
        }
      });

      if (error) {
        console.error('Pattern refresh edge function error:', error);
        await this.updateJobStatus(jobId, 'failed', {
          error_message: error.message || 'Pattern refresh failed'
        });
        return;
      }

      console.log('Pattern refresh completed:', data);
      
      // The edge function will handle updating the job status to completed
      
    } catch (error) {
      console.error('Pattern refresh processing failed:', error);
      await this.updateJobStatus(jobId, 'failed', {
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }
}

export const batchProcessingService = new BatchProcessingService();
